import { HStack } from "@zenchef/styled-system/jsx";
import { FC, ReactNode, forwardRef } from "react";
import Button from "../Button/Button";
import { IconName } from "@zenchef/icomoon";
import Icon from "../Icon/Icon";
import Tooltip from "../Tooltip/Tooltip";
import { Dialog } from "@ark-ui/react";

interface HintProps {
  hint?: string;
  hintIcon?: IconName;
}

interface HeaderAction {
  /**
   * Action to perform on click and icon to show in action button of the header, if undefined, button wont be shown
   */
  onClick: () => void;
  /**
   * Icon to show in action button of the header
   */
  icon: IconName;
  /**
   * Tooltip to show in action button of the header
   */
  tooltip?: string;
  testId?: string;
}
export interface HeaderExposedProps {
  /**
   * Title to show in the header
   */
  title?: string;
  /**
   * Action to perform on click and icon to show in action button of the header, if undefined, button wont be shown
   */
  headerAction?: HeaderAction;
  /**
   * Indicator to show in the header, if undefined, indicator wont be shown.
   * It can be any react node, however design guidelines recommend an Aroma Tag or Aroma Badge
   */
  indicator?: ReactNode;
  /**
   * Icon to show in the header left from the title, if undefined, icon won't be shown.
   * It can be any react node, however design guidelines recommend an Icon or ContextualIcon
   */
  titleIcon?: ReactNode;
  /**
   * Action to perform on click on chevron-left button in the header, if undefined, button wont be shown
   */
  backAction?: () => void;
}

export interface HeaderInternalProps extends HintProps, HeaderExposedProps {
  classes: Record<"header", string>;
  closeTrigger: JSX.Element;
  onTouchStart?: (e: React.TouchEvent) => void;
}

const HeaderActionButton: FC<HeaderAction> = ({
  onClick,
  icon,
  tooltip,
  testId,
}) => {
  if (tooltip) {
    return (
      <Tooltip title={tooltip}>
        <Button
          iconOnly
          hierarchy="secondary-subtler"
          onClick={onClick}
          testId={testId}
        >
          <Icon name={icon} />
        </Button>
      </Tooltip>
    );
  }

  return (
    <Button
      iconOnly
      hierarchy="secondary-subtler"
      onClick={onClick}
      testId={testId}
    >
      <Icon name={icon} />
    </Button>
  );
};

const HeaderComponent = ({
  classes,
  title,
  indicator,
  headerAction,
  backAction,
  hint,
  hintIcon,
  titleIcon,
  closeTrigger,
  onTouchStart,
}: HeaderInternalProps) => {
  return (
    <div onTouchStart={onTouchStart} className={classes.header}>
      {backAction ? (
        <Button
          iconOnly
          hierarchy="secondary-subtler"
          onClick={backAction}
          testId="drawer-back"
        >
          <Icon name="chevron-left" />
        </Button>
      ) : null}
      <HStack gap="gap.2" mr="auto" pl="padding.2">
        {titleIcon}
        {title ? <Dialog.Title>{title}</Dialog.Title> : null}
        {indicator}
      </HStack>
      <HStack gap="gap.2">
        {hint ? (
          <Dialog.Description asChild>
            <Tooltip title={hint}>
              <Icon
                name={hintIcon}
                fontSize="{sizes.2xs}"
                color="content.neutral.base.subtlest"
              />
            </Tooltip>
          </Dialog.Description>
        ) : null}
        {headerAction ? (
          <HeaderActionButton
            icon={headerAction.icon}
            tooltip={headerAction.tooltip}
            onClick={headerAction.onClick}
            testId={headerAction.testId}
          />
        ) : null}
        {closeTrigger}
      </HStack>
    </div>
  );
};

const Header = forwardRef<HTMLDivElement, HeaderInternalProps>((props, ref) => {
  return (
    <div ref={ref}>
      <HeaderComponent {...props} />
    </div>
  );
});

Header.displayName = "Header";

export default Header;
